import { select, call, takeLatest, put } from 'redux-saga/effects';

import { ProviderActions, StudentActions } from '../constants';
import { ProviderApi, StudentApi } from 'src/services/end-points';
import { StoreState } from '../reducers';
import { provider } from 'src/helpers/provider';
import { PaginationService } from '../../services/api-handlers/pagination';
import {
    DeleteProviderAction,
    GetProviderAction,
    GetProvidersAction,
    PersistProviderAction,
    CreateFilesProviderAction,
    GetFilesAction,
    DeleteFileProviderAction,
    GetProvidersSmallAction,
    createFilesProvider,
    GetProvidersConfirmedMetricsAction,
} from '../actions/provider-actions';
import sortingService from '../../services/sorting';
import {
    failureType,
    handleError,
    resolveApiCall,
    ResolverApiFailure,
    ResolverApiSuccess,
    successType,
} from '../../services/api-handlers/api-resolver';
import { SubmissionError } from 'redux-form';
import { makeFormErrors } from '../../components/redux-form/utils/make-form-errors';
import { convertValuesToFormData } from '../../components/redux-form/utils/values-to-form-data';
import { push } from 'connected-react-router';
import Router from '../../navigation/router';
import routes from '../../navigation/routes';
import { toast } from 'react-toastify';
import { exportToCsv } from '../../services/exports/csv';
import { groupProviderToCsv } from '../../helpers/provider/groupCsv';

function* handleFetchProviders(action: GetProvidersAction) {
    const oldData = yield select((state: StoreState) => state.providers.data);
    const { filters, sorting, page, itemsPerPage } = action.payload;

    yield PaginationService.setPageSize(itemsPerPage).resolveApiCall(action, page, oldData, () =>
        ProviderApi.getProviders({
            'order[fullName]': 'asc',
            ...filters,
            order: sortingService.makeOrder(sorting),
            page,
            itemsPerPage,
            pagination: itemsPerPage === -1 ? 0 : 1,
        }),
    );
}

function* handleFetchProvidersConfirmedMetrics(action: GetProvidersConfirmedMetricsAction) {
    const oldData = yield select((state: StoreState) => state.providers.data);
    const { filters, sorting, page, itemsPerPage } = action.payload;

    yield PaginationService.setPageSize(itemsPerPage).resolveApiCall(action, page, oldData, () =>
        ProviderApi.getProvidersConfirmedMetrics({
            'order[fullName]': 'asc',
            ...filters,
            order: sortingService.makeOrder(sorting),
            page,
            itemsPerPage,
            pagination: itemsPerPage === -1 ? 0 : 1,
        }),
    );
}

// The confirmed-metrics endpoint aggregates all sessions of every provider, so it
// is slow: asking for the whole list at once makes the backend answer with a 503.
// The export is pulled page by page instead and glued together before it is saved.
const CSV_CHUNK_SIZE = 200;

function* handleFetchProvidersCsv(action: GetProvidersConfirmedMetricsAction): any {
    const { filters, sorting } = action.payload;
    let page: number = action?.payload?.page ? +action.payload.page : 1;
    yield resolveApiCall(
        action,
        () =>
            ProviderApi.getProvidersConfirmedMetrics({
                'order[fullName]': 'asc',
                ...filters,
                order: sortingService.makeOrder(sorting),
                page,
                itemsPerPage: CSV_CHUNK_SIZE,
                pagination: 1,
            }),
        function* (data) {
            const dataAll = action.all ? [...action.all, ...data.payload] : [...data.payload];

            if (data.payload.length === CSV_CHUNK_SIZE) {
                const actionFiles: GetProvidersConfirmedMetricsAction = {
                    type: ProviderActions.STAFF_FETCH_PROVIDERS_CSV,
                    payload: {
                        filters,
                        sorting,
                        page: ++page,
                        itemsPerPage: CSV_CHUNK_SIZE,
                    },
                    all: dataAll,
                };

                yield call(handleFetchProvidersCsv, actionFiles);
            } else {
                exportToCsv('confirmed-metrics.csv', groupProviderToCsv(dataAll));
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                toast.error(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
            }
            handleError(result);
        },
    );
}

function* handleFetchProvidersSmall(action: GetProvidersSmallAction) {
    const oldData = yield select((state: StoreState) => state.providers.data);
    const { filters, sorting, page, itemsPerPage } = action.payload;

    yield PaginationService.setPageSize(itemsPerPage).resolveApiCall(action, page, oldData, () =>
        ProviderApi.getProvidersSmall({
            ...filters,
            order: sortingService.makeOrder(sorting),
            page,
            itemsPerPage,
            pagination: itemsPerPage === -1 ? 0 : 1,
        }),
    );
}
function* handleFetchSupervisorProviders(action: GetProvidersAction) {
    const oldData = yield select((state: StoreState) => state.providers.supervisorProviders);
    const { filters, sorting, page, itemsPerPage } = action.payload;

    yield PaginationService.setPageSize(itemsPerPage).resolveApiCall(action, page, oldData, () =>
        ProviderApi.getSuperViserProviders({
            ...filters,
            order: sortingService.makeOrder(sorting),
            page,
            itemsPerPage,
            pagination: itemsPerPage === -1 ? 0 : 1,
        }),
    );
}

function* handleFetchProvider(action: GetProviderAction) {
    const {
        payload: { id },
    } = action;

    yield resolveApiCall(action, () => ProviderApi.getProvider(id));
}
function* handleDeleteProvider(action: DeleteProviderAction) {
    const {
        payload: { id, onSuccess, onError },
    } = action;

    yield resolveApiCall(
        action,
        () => ProviderApi.deleteProvider(id),
        function* () {
            if (onSuccess) {
                onSuccess();
                yield put({ type: successType(ProviderActions.STAFF_DELETE_PROVIDER), payload: { id } });
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                if (onError) {
                    onError(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
                }
            }
            handleError(result);
        },
    );
}

function* handlePersistProvider(action: PersistProviderAction) {
    const { payload } = action;
    const {
        data: { id },
        files,
        callbacks,
    } = payload;

    const filesToSend = provider(payload.data);

    yield resolveApiCall(
        action,
        () => (id ? ProviderApi.updateProvider(id, filesToSend) : ProviderApi.createProvider(filesToSend)),
        function* (data: ResolverApiSuccess) {
            yield put(
                createFilesProvider(
                    data.payload.userId,
                    'OTHER',
                    files.files,
                    () => false,
                    (errorMessage: string) => {
                        toast.error(errorMessage);
                    },
                ),
            );

            yield put(
                createFilesProvider(
                    data.payload.userId,
                    'SIGNDOC',
                    files.signDoc ? files.signDoc : [],
                    () => false,
                    (errorMessage: string) => {
                        toast.error(errorMessage);
                    },
                ),
            );

            yield call(callbacks.resolve);
            if (!id) {
                yield put(push(Router.generate(routes.PROVIDERS_LIST)));
            } else {
                yield put(push(Router.generate(routes.PROVIDER_DETAIL, { id, tab: 'main-info', subId: '' })));
            }
        },
        function* (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                yield call(
                    callbacks.reject,
                    result.error instanceof SubmissionError
                        ? result.error
                        : new SubmissionError({
                              _error: result.error.message ?? 'Incorrect response',
                              ...makeFormErrors(result.error.response?.data),
                          }),
                );
            }
            yield handleError(result);
        },
    );
}

function* handleCreateFilesProvider(action: CreateFilesProviderAction) {
    const {
        payload: { id, requestType, files, onSuccess, onError },
    } = action;

    let requestFunction: any = (file: any) => {
        const objToForm = { user: id, attachFile: file };
        const formData = convertValuesToFormData(objToForm);
        return ProviderApi.createFilesProvider(formData);
    };
    switch (requestType) {
        case 'SIGNDOC':
            requestFunction = (file: any) => {
                const objToForm = { signDocFile: file, userId: id };
                const formData = convertValuesToFormData(objToForm);

                return ProviderApi.createSignFilesProvider(formData, id);
            };
            break;
        default:
            requestFunction = (file: any) => {
                const objToForm = { user: id, attachFile: file };
                const formData = convertValuesToFormData(objToForm);
                return ProviderApi.createFilesProvider(formData);
            };
            break;
    }

    for (let i = 0; i < files.length; i++) {
        if (files[i]?.id || files[i]?.attach || !files[i].type) {
            continue;
        }

        yield resolveApiCall(
            action,
            () => requestFunction(files[i]),
            function* (data: ResolverApiSuccess) {
                if (onSuccess) {
                    onSuccess();
                    yield put({
                        type: successType(ProviderActions.STAFF_CREATE_FILES_PROVIDER_TYPE),
                        payload: { ...data, requestType },
                    });
                }
            },
            function (result: ResolverApiFailure) {
                if (result.error && result.error.response && result.error.response.status) {
                    if (onError) {
                        onError(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
                    }
                }
            },
        );
    }
}
function* handleDeleteFileProvider(action: DeleteFileProviderAction) {
    const {
        payload: { id, onSuccess, onError },
    } = action;

    yield resolveApiCall(
        action,
        () => ProviderApi.deleteFileProvider(id),
        function (data) {
            if (onSuccess) {
                onSuccess();
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                if (onError) {
                    onError(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
                }
            }
            handleError(result);
        },
    );
}
function* handleGetFilesProvider(action: GetFilesAction) {
    const {
        payload: { id },
    } = action;

    yield resolveApiCall(action, () => ProviderApi.getFilesProvider(id));
}
function* handleUpdateProvider(action: PersistProviderAction) {
    const { payload } = action;
    const { data, callbacks } = payload;

    yield resolveApiCall(
        action,
        () => ProviderApi.patchProvider(data),
        function* () {
            yield put({ type: successType(ProviderActions.STAFF_UPDATE_PROVIDER), payload: data });
            yield call(callbacks.resolve);
        },
        function* (result: ResolverApiFailure) {
            yield put({ type: failureType(ProviderActions.STAFF_UPDATE_PROVIDER), error: result?.error?.message });
            if (result.error && result.error.response && result.error.response.status) {
                return yield call(
                    callbacks.reject,
                    result.error instanceof SubmissionError
                        ? result.error
                        : new SubmissionError({
                              _error: result.error.message ?? 'Incorrect response',
                          }),
                );
            }
            yield handleError(result);
        },
    );
}

export default function* () {
    yield takeLatest(ProviderActions.STAFF_FETCH_PROVIDERS, handleFetchProviders);
    yield takeLatest(ProviderActions.STAFF_FETCH_PROVIDERS_CONFIRMED_METRICS, handleFetchProvidersConfirmedMetrics);
    yield takeLatest(ProviderActions.STAFF_FETCH_PROVIDERS_CSV, handleFetchProvidersCsv);
    yield takeLatest(ProviderActions.STAFF_FETCH_PROVIDERS_SMALL, handleFetchProvidersSmall);
    yield takeLatest(ProviderActions.STAFF_FETCH_SUPERVISOR_PROVIDERS, handleFetchSupervisorProviders);
    yield takeLatest(ProviderActions.STAFF_FETCH_PROVIDER, handleFetchProvider);
    yield takeLatest(ProviderActions.STAFF_DELETE_PROVIDER, handleDeleteProvider);
    yield takeLatest(ProviderActions.STAFF_PERSIST_PROVIDER, handlePersistProvider);
    yield takeLatest(ProviderActions.STAFF_DELETE_FILE_PROVIDER, handleDeleteFileProvider);
    yield takeLatest(ProviderActions.STAFF_CREATE_FILES_PROVIDER, handleCreateFilesProvider);
    yield takeLatest(ProviderActions.STAFF_FETCH_FILES_PROVIDER, handleGetFilesProvider);
    yield takeLatest(ProviderActions.STAFF_UPDATE_PROVIDER, handleUpdateProvider);
}
